import { AppMark } from "../components/AppMark";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { BottomNav } from "../components/BottomNav";
import { TvBoard } from "../components/board/TvBoard";
import { fetchBoard, type BoardWindowKey } from "../lib/engageBoard";
import { useLeaderboardVisible, useSites, useUnits } from "../hooks/useUnits";
import { fmtWeight, rateParts } from "../lib/units";

// Leaderboard — M6 addictive layer. Data source is GET /api/engage/board
// (percentile bands, PB-day sparkle, most-improved, jobs) with a hard
// fallback to the plain GET /api/leaderboard whenever engage/board 404s
// (see lib/engageBoard.ts's fetchBoard) — that fallback renders the ORIGINAL
// M5b board unchanged: numeric ranks for row 4+, no sparkle/improvement
// badges, Most Improved tab explains it needs the newer endpoint. `?tv=1`
// swaps the whole page for the fullscreen kiosk wall (components/board/
// TvBoard.tsx) — read directly off window.location.search rather than
// wiring a validateSearch into router.tsx, since this is the only page that
// cares about a query param at all and a plain full navigation (the header
// button below is a bare <a>, not a router Link) is exactly what a
// TV/kiosk device wants on first load anyway.
const WINDOWS: { key: BoardWindowKey; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7 days" },
  { key: "all", label: "All time" },
];

const MEDALS = ["🥇", "🥈", "🥉"];

function fmtDuration(ms: number): string {
  const h = Math.floor((ms || 0) / 3600000);
  const m = Math.floor(((ms || 0) % 3600000) / 60000);
  return h ? `${h}h ${m}m` : `${m}m`;
}

function isTvMode(): boolean {
  return new URLSearchParams(window.location.search).get("tv") === "1";
}

export function BoardPage() {
  const units = useUnits();
  const rateUnit = rateParts(0, units).unit;
  const rateNum = (gpm: number) => rateParts(gpm, units, 1).value;
  const { player, isOwner, loading, role } = useAuth();
  const [windowKey, setWindowKey] = useState<BoardWindowKey>("today");
  const [harvestId, setHarvestId] = useState("");
  const [siteId, setSiteId] = useState("");
  const sites = useSites();
  const [boardTab, setBoardTab] = useState<"leaderboard" | "improved">("leaderboard");

  const signedIn = Boolean(player) || isOwner;
  const isManagerPlus = role === "manager" || role === "admin";
  // Owner/managers always; workers only once the owner makes it public. The
  // API 403s hidden viewers regardless, this just avoids asking.
  const canSee = useLeaderboardVisible();

  const { data, isLoading } = useQuery({
    queryKey: ["engage-board", windowKey, harvestId, siteId],
    queryFn: () => fetchBoard(windowKey, harvestId, siteId),
    enabled: !loading && signedIn && canSee,
  });

  if (isTvMode()) {
    if (loading) return null;
    if (!signedIn) {
      return (
        <main className="page">
          <div className="panel small muted center">Sign in from Log to see the board.</div>
        </main>
      );
    }
    if (!canSee) {
      return (
        <main className="page">
          <div className="panel small muted center" data-testid="board-hidden">The leaderboard is hidden.</div>
        </main>
      );
    }
    return <TvBoard />;
  }

  const usingEngage = data?.source === "engage";
  const board = data?.data.board ?? [];
  const harvests = data?.data.harvests ?? [];
  const mostImproved = data?.data.most_improved ?? [];

  return (
    <>
      <header className="top-bar">
        <div className="brand"><AppMark />Crew board</div>
        {signedIn && isManagerPlus && (
          <div className="top-actions">
            <a href="/board?tv=1" className="link-btn" data-testid="tv-mode-open">📺 TV</a>
          </div>
        )}
      </header>
      <main className="page">
        <div className="hero">
          <h1>Leaderboard</h1>
          <p>Total trimmed weight wins. Rate and QA settle the arguments.</p>
        </div>

        {!loading && !signedIn && (
          <div className="panel small muted center">Sign in from Log to see the board.</div>
        )}

        {signedIn && !canSee && (
          <div className="panel small muted center" data-testid="board-hidden">
            The leaderboard is only visible to managers in this workspace.
          </div>
        )}

        {signedIn && canSee && (
          <>
            <div className="tab-bar">
              <button
                type="button" data-testid="board-tab-leaderboard"
                className={`tab-btn${boardTab === "leaderboard" ? " active" : ""}`}
                onClick={() => setBoardTab("leaderboard")}
              >
                Leaderboard
              </button>
              <button
                type="button" data-testid="board-tab-improved"
                className={`tab-btn${boardTab === "improved" ? " active" : ""}`}
                onClick={() => setBoardTab("improved")}
              >
                Most Improved
              </button>
            </div>

            <div className="panel">
              <div className="chip-grid" data-testid="window-chips">
                {WINDOWS.map((w) => (
                  <button
                    key={w.key} type="button"
                    className={`chip${windowKey === w.key ? " active" : ""}`}
                    onClick={() => setWindowKey(w.key)}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
              <div className="chip-grid" style={{ marginTop: 8 }} data-testid="harvest-chips">
                <button type="button" className={`chip${harvestId === "" ? " active" : ""}`} onClick={() => setHarvestId("")}>
                  All harvests
                </button>
                {harvests.map((h) => (
                  <button
                    key={h.id} type="button"
                    className={`chip${harvestId === String(h.id) ? " active" : ""}`}
                    onClick={() => setHarvestId(String(h.id))}
                  >
                    {h.name}{h.active ? "" : " ✓"}
                  </button>
                ))}
              </div>
              {/* Only multi-site orgs see this; single-site orgs have no sites. */}
              {sites.length > 0 && (
                <div className="chip-grid" style={{ marginTop: 8 }} data-testid="site-chips">
                  <button type="button" className={`chip${siteId === "" ? " active" : ""}`} onClick={() => setSiteId("")}>
                    All sites
                  </button>
                  {sites.map((st) => (
                    <button
                      key={st.id} type="button"
                      data-testid={`site-chip-${st.id}`}
                      className={`chip${siteId === String(st.id) ? " active" : ""}`}
                      onClick={() => setSiteId(String(st.id))}
                    >
                      {st.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {boardTab === "leaderboard" && (
              <div className="panel">
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th /><th>Trimmer</th><th>{units === "imperial" ? "Total" : "Total g"}</th><th>Time</th>
                        <th>Avg {rateUnit}</th><th>Best {rateUnit}</th><th>Runs</th><th>QA</th><th>Badges</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading && <tr><td colSpan={9} className="muted">Loading…</td></tr>}
                      {!isLoading && board.length === 0 && (
                        <tr><td colSpan={9} className="muted">No runs in this window yet.</td></tr>
                      )}
                      {board.map((p, i) => (
                        <tr key={p.id} data-testid="board-row">
                          <td>
                            {i < 3 ? (
                              MEDALS[i]
                            ) : usingEngage ? (
                              <span className="pill" data-testid="percentile-chip">{p.percentile_band ?? "—"}</span>
                            ) : (
                              i + 1
                            )}
                          </td>
                          <td>
                            <b>{p.name}</b>
                            {usingEngage && p.is_pb_day && (
                              <span title="Personal best today!"> ✨</span>
                            )}
                            {usingEngage && p.most_improved_pct != null && p.most_improved_pct > 0 && (
                              <span className="chip-note" title="vs their own recent average">
                                ▲{p.most_improved_pct.toFixed(0)}%
                              </span>
                            )}
                            {p.badges > 0 && <span title={`${p.badges} job badge${p.badges === 1 ? "" : "s"}`}> 🏅</span>}
                          </td>
                          <td><b>{fmtWeight(p.total_grams, units, 0)}</b></td>
                          <td>{fmtDuration(p.total_active_ms)}</td>
                          <td>
                            {rateNum(p.avg_gpm)}
                            {p.adj_gpm ? (
                              <span className="muted small" title="adjusted for nug size & style"> · {rateNum(p.adj_gpm)} adj</span>
                            ) : null}
                          </td>
                          <td>{rateNum(p.best_gpm)}</td>
                          <td>{p.runs}</td>
                          <td>{p.avg_qa ? p.avg_qa.toFixed(1) : "—"}</td>
                          <td data-testid="badge-count">{p.badges || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {boardTab === "improved" && (
              <div className="panel">
                <h2>Most improved</h2>
                {isLoading && <div className="muted small">Loading…</div>}
                {!isLoading && mostImproved.length === 0 && (
                  <div className="muted small" data-testid="most-improved-empty">
                    {usingEngage
                      ? "Not enough run history yet to show most-improved — keep trimming and check back in a few days."
                      : "Most Improved needs the engage board endpoint, which isn’t available on this server yet."}
                  </div>
                )}
                {!isLoading && mostImproved.length > 0 && (
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead><tr><th /><th>Trimmer</th><th>Improvement</th></tr></thead>
                      <tbody>
                        {mostImproved.map((e, i) => (
                          <tr key={e.player_id} data-testid="most-improved-row">
                            <td>{i === 0 ? "🚀" : i + 1}</td>
                            <td><b>{e.name}</b></td>
                            <td>▲ {e.improvement_pct.toFixed(1)}% vs their own average</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="muted small" style={{ marginTop: 10 }}>
                  Self vs self — celebrating growth against each trimmer&rsquo;s own history, not against each other.
                </div>
              </div>
            )}
          </>
        )}
      </main>
      <BottomNav />
    </>
  );
}
